// Console-backed logger with pino-compatible level names, so call sites read the same on client and server.
export type LogLevel = "trace" | "debug" | "info" | "warn" | "error" | "silent";

const validLevels: LogLevel[] = ["trace", "debug", "info", "warn", "error", "silent"];

export function toLogLevel(value: string | undefined): LogLevel {
  return validLevels.includes(value as LogLevel) ? (value as LogLevel) : "warn";
}

const levelOrder: Record<LogLevel, number> = {
  trace: 10,
  debug: 20,
  info: 30,
  warn: 40,
  error: 50,
  silent: 100,
};

let currentLevel: LogLevel = "warn";

export function setClientLogLevel(level: LogLevel) {
  currentLevel = level;
}

function shouldLog(level: LogLevel): boolean {
  return levelOrder[level] >= levelOrder[currentLevel];
}

// Component tag is prefixed onto the message text (`[component] msg`) rather than folded into
// the context object, so a plain-string call site still carries it without gaining an object arg.
function log(
  level: Exclude<LogLevel, "silent">,
  consoleMethod: "debug" | "info" | "warn" | "error",
  component?: string,
) {
  return (objOrMsg?: unknown, msg?: string) => {
    if (!shouldLog(level)) return;
    const tag = (text: string) => (component ? `[${component}] ${text}` : text);
    if (typeof objOrMsg === "string" && msg === undefined) {
      console[consoleMethod](tag(objOrMsg));
      return;
    }
    if (msg === undefined) {
      console[consoleMethod](objOrMsg);
      return;
    }
    console[consoleMethod](tag(msg), objOrMsg);
  };
}

function buildLogger(component?: string) {
  return {
    trace: log("trace", "debug", component),
    debug: log("debug", "debug", component),
    info: log("info", "info", component),
    warn: log("warn", "warn", component),
    error: log("error", "error", component),
  };
}

export const logger = buildLogger();

// Per-module logger: tags every line with `[component]` so client console output can be
// attributed the same way server logs are via logger.server.ts's component-bound children.
export function createLogger(component: string) {
  return buildLogger(component);
}
